import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, LogIn, LogOut, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { SelfieStep } from '@/components/staffAttendance/SelfieStep';
import { StatusBadge } from '@/components/staffAttendance/Badges';
import { staffAttApi, errMsg, fmtTime, kindLabel } from '@/services/staffAttendanceApi';
import { GuideMenu } from '@/components/staffAttendance/GuideMenu';
import { getGPS } from '@/services/transportApi';

const PunchRow = ({ p }) => (
  <div className="flex items-center justify-between py-2 border-b last:border-0" data-testid={`my-att-punch-${p.punch_id}`}>
    <div>
      <div className="font-semibold">{p.kind === 'in' ? 'Check In' : 'Check Out'} · {fmtTime(p.time)}</div>
      {p.status === 'rejected' && <div className="text-xs text-red-600" data-testid={`my-att-reason-${p.punch_id}`}>Rejected{p.decided_by_name ? ` by ${p.decided_by_name}` : ''}: {p.reason}</div>}
      {p.status === 'approved' && p.decided_by_name && <div className="text-xs text-gray-500">Approved by {p.decided_by_name}</div>}
    </div>
    <StatusBadge status={p.status} testId={`my-att-status-${p.punch_id}`} />
  </div>
);

export default function MyAttendance() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [err, setErr] = useState('');
  const [taking, setTaking] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => staffAttApi.myToday().then((d) => { setMe(d); setErr(''); }).catch((e) => setErr(errMsg(e, 'Could not load your attendance'))), []);
  useEffect(() => { load(); }, [load]);

  const submit = async (selfie, liveness) => {
    setBusy(true);
    try {
      const gps = await getGPS(10000);
      const r = await staffAttApi.myPunch({ gps, selfie, liveness });
      toast.success(`${r.kind === 'in' ? 'Check In' : 'Check Out'}${r.session_no > 1 ? ` (session ${r.session_no})` : ''} recorded${r.on_site === false ? ' (off-site)' : ''} — waiting for supervisor approval`);
      setTaking(false); load();
    } catch (e) { toast.error(errMsg(e, 'Could not record attendance')); } finally { setBusy(false); }
  };

  const kind = me?.next_kind;
  const label = kindLabel(kind, me?.session_count);
  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="my-attendance-page">
      <header className="bg-[#1A1F2E] text-white shadow-lg pt-10 sm:pt-0">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate('/admin/dashboard')} className="text-white hover:bg-white/10" data-testid="back-button"><ArrowLeft className="w-5 h-5" /></Button>
          <h1 className="text-xl font-bold">My Attendance</h1>
          <GuideMenu which={['staff']} className="ml-auto" />
        </div>
      </header>
      <main className="max-w-xl mx-auto px-4 py-6 space-y-4">
        {err && <Card className="border-red-200 bg-red-50"><CardContent className="p-4 text-red-700" data-testid="my-att-error">{err}</CardContent></Card>}
        {!me && !err && <Loader2 className="w-6 h-6 animate-spin mx-auto text-amber-600" />}
        {me && (
          <>
            <Card className="border-0 shadow-md"><CardContent className="p-5">
              <div className="text-sm text-gray-500">{me.section} · {me.staff_id}</div>
              <div className="text-2xl font-bold" data-testid="my-att-name">{me.staff_name}</div>
              {me.pool_device_only && <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800" data-testid="my-att-pool-device-only">Contract / Contractor Pool staff must Check In / Check Out on the <b>Section Device</b> of the section you work in — own phone is not allowed.</div>}
              {!taking && !me.pool_device_only && (kind ? (
                <Button onClick={() => setTaking(true)} className={`mt-4 w-full h-14 text-lg ${kind === 'in' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-indigo-600 hover:bg-indigo-700'}`} data-testid="my-att-punch-btn">
                  {kind === 'in' ? <LogIn className="w-5 h-5 mr-2" /> : <LogOut className="w-5 h-5 mr-2" />}{label}
                </Button>
              ) : (
                <div className="mt-4 flex items-center gap-2 text-emerald-700 font-semibold" data-testid="my-att-all-done"><CheckCircle2 className="w-5 h-5" />All {me.max_sessions} Check In / Check Out sessions recorded for today</div>
              ))}
              {!taking && !me.pool_device_only && me.session_count > 0 && <div className="mt-2 text-xs text-gray-500" data-testid="my-att-session-count">Session {Math.min(me.session_count + (kind === 'in' ? 1 : 0), me.max_sessions)} of up to {me.max_sessions} today</div>}
              {me.after_midnight && <div className="mt-2 rounded-lg bg-indigo-50 border border-indigo-200 px-3 py-2 text-sm text-indigo-800" data-testid="my-att-after-midnight">OT check-out for <b>{me.day}</b> — your session from yesterday is still open. Check Out before {me.day_close} AM to count these hours.</div>}
              {taking && (
                <div className="mt-4 space-y-2">
                  <p className="text-sm text-gray-600">Take a selfie — look straight at the camera{me.liveness_required ? ', then turn your head when the arrow shows' : ''}. Turn on location; punches away from the site go to your supervisor as Off-site.</p>
                  <SelfieStep busy={busy} liveness={!!me.liveness_required} onSubmit={submit} onCancel={() => setTaking(false)} submitLabel={`Submit ${label}`} testId="my-att-selfie" />
                  <button onClick={() => setTaking(false)} className="text-sm text-gray-500 underline" data-testid="my-att-cancel">Cancel</button>
                </div>
              )}
            </CardContent></Card>
            <Card className="border-0 shadow-md"><CardContent className="p-5">
              <h2 className="font-semibold mb-2">Today</h2>
              {me.punches.length === 0 ? <p className="text-sm text-gray-400" data-testid="my-att-none">No punches yet today.</p> : me.punches.map((p) => <PunchRow key={p.punch_id} p={p} />)}
            </CardContent></Card>
          </>
        )}
      </main>
    </div>
  );
}
